import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Typography,
  Space,
  Checkbox,
} from "antd";
import { MailOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  useVerifyOTPMutation,
  useAdminVerifyOTPMutation,
  useResendOTPMutation,
} from "../../store/api/apiSlice";
import {
  generateDeviceFingerprint,
  storeTrustedDeviceFingerprint,
} from "../../utils";
import { extractAccessTokenFromLoginPayload } from "../../utils/extractAccessToken";
import {
  otpConsoleHintOnce,
  otpDebugLog,
  otpProblemLog,
} from "../../utils/otpDebugLog";

const { Title, Text } = Typography;

const OTPVerificationModal = ({
  visible,
  onCancel,
  onSuccess,
  email,
  userType,
  onResendSuccess,
  loginDeviceFingerprint,
}) => {
  const [form] = Form.useForm();

  const [countdown, setCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  /** Default on: server only registers a trusted device when verify-otp includes trust_device. */
  const [trustDevice, setTrustDevice] = useState(true);
  const [isGeneratingFingerprint, setIsGeneratingFingerprint] = useState(false);

  const [verifyOTP, { isLoading: isVerifying }] = useVerifyOTPMutation();
  const [verifyAdminOTP, { isLoading: isVerifyingAdmin }] =
    useAdminVerifyOTPMutation();
  const [resendOTP, { isLoading: isResendingOTP }] = useResendOTPMutation();

  useEffect(() => {
    if (visible && countdown === 0) {
      setCountdown(30);
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      otpConsoleHintOnce();
      setTrustDevice(true);
    }
  }, [visible]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleVerifyOTP = async (values) => {
    try {
      let resolvedDevice = null;

      if (loginDeviceFingerprint?.hash && loginDeviceFingerprint.hash !== "unknown") {
        resolvedDevice = {
          hash: loginDeviceFingerprint.hash,
          fingerprint: loginDeviceFingerprint.fingerprint,
        };
        otpDebugLog("otp.verify.device", {
          source: "login_snapshot",
          hashPreview: `${String(resolvedDevice.hash).slice(0, 12)}…`,
        });
      } else {
        setIsGeneratingFingerprint(true);
        try {
          resolvedDevice = await generateDeviceFingerprint();
          if (!resolvedDevice?.hash || resolvedDevice.hash === "unknown") {
            throw new Error("Failed to generate device fingerprint");
          }
          otpDebugLog("otp.verify.device", {
            source: "generated_in_modal",
            hashPreview: `${String(resolvedDevice.hash).slice(0, 12)}…`,
            isFallback: Boolean(resolvedDevice.isFallback),
          });
        } catch (error) {
          otpDebugLog("otp.verify.device", {
            source: "generate_failed",
            message: error?.message,
          });
          message.warning(
            "Device fingerprint generation failed. Trust device and OTP binding may not work; you can still try verifying the code.",
          );
          resolvedDevice = null;
        } finally {
          setIsGeneratingFingerprint(false);
        }
      }

      const requestData = {
        email,
        otp_code: values.otp_code,
        type: userType,
        ...(resolvedDevice?.hash &&
          resolvedDevice.hash !== "unknown" && {
            device_fingerprint: resolvedDevice.hash,
            device_data: resolvedDevice.fingerprint,
          }),
        ...(trustDevice &&
          resolvedDevice?.hash &&
          resolvedDevice.hash !== "unknown" && {
            trust_device: true,
          }),
      };

      otpDebugLog("otp.verify.request", {
        email,
        type: userType,
        trustDevice,
        includesDevicePayload: Boolean(
          resolvedDevice?.hash && resolvedDevice.hash !== "unknown",
        ),
        includesTrustFlag: Boolean(
          trustDevice &&
            resolvedDevice?.hash &&
            resolvedDevice.hash !== "unknown",
        ),
      });

      let response;
      if (userType === "admin") {
        try {
          response = await verifyAdminOTP(requestData).unwrap();
        } catch (adminErr) {
          if (adminErr?.status === 404) {
            otpDebugLog("otp.verify.admin_path", {
              note: "/admin/verify-otp not found, using /auth/verify-otp",
            });
            response = await verifyOTP(requestData).unwrap();
          } else {
            throw adminErr;
          }
        }
      } else {
        response = await verifyOTP(requestData).unwrap();
      }

      const successPayload = response?.data ?? response;
      const accessToken = extractAccessTokenFromLoginPayload(successPayload);
      const isVerified =
        response?.success === true || Boolean(accessToken);

      otpDebugLog("otp.verify.response", {
        successFlag: response?.success,
        hasAccessToken: Boolean(accessToken),
        isVerified,
        topLevelKeys:
          response && typeof response === "object"
            ? Object.keys(response)
            : [],
      });

      if (isVerified) {
        if (trustDevice && resolvedDevice?.hash) {
          storeTrustedDeviceFingerprint(resolvedDevice, 30);
        }
        message.success("OTP verified successfully!");
        onSuccess(successPayload);
        form.resetFields();
        onCancel();
      } else {
        otpDebugLog("otp.verify.unexpected_shape", {
          responsePreview:
            typeof response === "object" ? JSON.stringify(response).slice(0, 400) : response,
        });
        otpProblemLog("otp.verify.unexpected_shape", {
          successFlag: response?.success,
          hasAccessToken: Boolean(accessToken),
          topLevelKeys:
            response && typeof response === "object"
              ? Object.keys(response)
              : [],
        });
        message.error(
          "Unexpected verification response. Please try again or contact support.",
        );
      }
    } catch (error) {
      otpDebugLog("otp.verify.error", {
        status: error?.status,
        message: error?.data?.message || error?.message,
        data: error?.data,
      });
      otpProblemLog("otp.verify.error", {
        status: error?.status,
        message: error?.data?.message || error?.message,
        data: error?.data,
      });
      message.error(
        error?.data?.message || "Failed to verify OTP. Please try again.",
      );
    } finally {
      setIsGeneratingFingerprint(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsResending(true);

      const response = await resendOTP({
        email,
        type: userType,
      }).unwrap();

      otpDebugLog("otp.resend.response", {
        success: response?.success,
        keys:
          response && typeof response === "object" ? Object.keys(response) : [],
      });

      if (response.success) {
        message.success("OTP resent successfully! Please check your email.");
        setCountdown(30);
        onResendSuccess?.(response.data);
      }
    } catch (error) {
      otpDebugLog("otp.resend.error", {
        status: error?.status,
        message: error?.data?.message || error?.message,
        data: error?.data,
      });
      otpProblemLog("otp.resend.error", {
        status: error?.status,
        message: error?.data?.message || error?.message,
        data: error?.data,
      });
      message.error(
        error?.data?.message || "Failed to resend OTP. Please try again.",
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setTrustDevice(true);
    onCancel();
  };

  return (
    <Modal
      title={
        <div className="text-center">
          <Title level={3} className="mb-2">
            Verify Your Identity
          </Title>
          <h4 className="mb-6">
            We've sent a verification code to <strong>{email}</strong>
          </h4>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={400}
      centered
      closable={true}
      forceRender
    >
      <div className="mb-4 flex items-start justify-center gap-3">
        <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50">
          <MailOutlined className="text-lg text-blue-500" />
        </div>
        <Text type="secondary" className="text-left leading-6">
          Enter the 6-digit code sent to your email address
        </Text>
      </div>
      <Form
        form={form}
        onFinish={handleVerifyOTP}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          name="otp_code"
          rules={[
            { required: true, message: "Please enter the OTP code!" },
            { len: 6, message: "OTP code must be 6 digits!" },
            {
              pattern: /^\d+$/,
              message: "OTP code must contain only numbers!",
            },
          ]}
        >
          <Input
            placeholder="Enter 6-digit OTP"
            className="h-12 text-md font-medium"
            maxLength={6}
            autoComplete="off"
          />
        </Form.Item>
        <Form.Item className="mb-4">
          <Checkbox
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
            className="mb-3"
          >
            <div className="flex items-center">
              <span className="text-sm">Trust this device for 30 days</span>
            </div>
          </Checkbox>
          {trustDevice && (
            <div className="ml-6 mb-3">
              <Text type="secondary" className="text-xs">
                The server remembers this device for about 30 days so you can
                skip OTP next time. Uncheck on shared or public computers.
              </Text>
            </div>
          )}
        </Form.Item>
        <Form.Item className="mb-4">
          <Button
            type="primary"
            htmlType="submit"
            className="h-12 w-full rounded-lg font-medium"
            loading={
              isVerifying ||
              isVerifyingAdmin ||
              isGeneratingFingerprint
            }
            disabled={
              isVerifying ||
              isVerifyingAdmin ||
              isGeneratingFingerprint
            }
          >
            {isVerifying || isVerifyingAdmin || isGeneratingFingerprint
              ? isGeneratingFingerprint
                ? "Generating device fingerprint..."
                : "Verifying..."
              : "Verify OTP"}
          </Button>
        </Form.Item>
      </Form>
      <div className="text-center">
        <Space direction="vertical" size="small">
          <Text type="secondary">Didn't receive the code?</Text>
          <Button
            type="link"
            icon={<ReloadOutlined />}
            onClick={handleResendOTP}
            disabled={countdown > 0 || isResending}
            loading={isResendingOTP}
            className="p-0 h-auto"
          >
            {countdown > 0 ? `Resend in ${countdown}s` : "Resend OTP"}
          </Button>
        </Space>
      </div>
      <div className="mt-6 p-3 bg-gray-50 rounded-lg">
        <Text type="secondary" className="text-xs">
          <strong>Note:</strong> The verification code will expire in 10
          minutes. Make sure to check your spam folder if you don't see the
          email.
        </Text>
      </div>
    </Modal>
  );
};

export default OTPVerificationModal;
